'use client'
import Link from 'next/link'
import { useDispatch } from 'react-redux'
import { useRouter, usePathname } from 'next/navigation'
import { useFormik } from 'formik'
import { TwitterXSVG, LinkedInSVG, FooterInstagramSVG, ValloteLogoSVG } from '@/src/ui/AllSVG'
import ImageComponent from '../../common/ImageComponent'
import { Button } from '@heroui/react'
import {
    getAboutUsPath,
    getMembershipPath,
    getPrivacyPolicyPath,
    getRefundPolicyPath,
    getShippingPolicyPath,
    getTermsAndConditionsPath,
    getCookiePolicyPath,
    getContactUsPath,
    getCategoriesPath,
    getCartPath,
    getUserDashboardPath,
    getWishlistPath,
    getMyOrdersPath,
} from '@/src/routes/routes'
import { getFaqPath } from '@/src/routes/routes'
import { useSubscribeToNewsletterMutation } from '@/src/store/rtkQueries/userPostAPI'
import { toast } from '@/src/lib/toast'
import { IMegaMenuCategoriesAPIResponseData } from '@/src/types/megaMenu'
import { useAuth } from '@/src/hooks/useAuth'
import { openModal } from '@/src/store/slices/allModalSlice'
import { newsletterValidationSchema } from '@/src/validations/formvalidations'

const aboutLinks = [
    { label: 'About Us', href: getAboutUsPath() },
    { label: 'Membership', href: getMembershipPath() },
    { label: 'Contact Us', href: getContactUsPath() },
    { label: 'FAQ', href: getFaqPath() },
    { label: 'Terms & Conditions', href: getTermsAndConditionsPath() },
    { label: 'Privacy Policy', href: getPrivacyPolicyPath() },
    { label: 'Returns & Refund Policy', href: getRefundPolicyPath() },
    { label: 'Shipping Policy', href: getShippingPolicyPath() },
    { label: 'Cookie Policy', href: getCookiePolicyPath() },
]


const accountLinks = [
    { label: 'My Account', href: getUserDashboardPath() },
    { label: 'Cart', href: getCartPath() },
    { label: 'Wishlist', href: getWishlistPath() },
    { label: 'Check Order', href: getMyOrdersPath() },
]

interface FooterProps {
    megaMenuData: IMegaMenuCategoriesAPIResponseData[]
    initialIsAuthenticated?: boolean
    xLink?: string
    instagramLink?: string
    linkedInLink?: string
    platformDescription?: string
}
export default function Footer({ megaMenuData, initialIsAuthenticated, xLink, instagramLink, linkedInLink, platformDescription }: FooterProps) {
    const router = useRouter()
    const pathname = usePathname()
    const dispatch = useDispatch()
    const { isAuthenticated } = useAuth(initialIsAuthenticated)
    const showMobileBottomNav = !pathname.startsWith('/dashboard')
    const shopLinks = megaMenuData.map((item) => ({ label: item.name, href: `${getCategoriesPath()}?category=${item?.slug}` }))
    const [subscribeToNewsletter, { isLoading: isSubscribing }] = useSubscribeToNewsletterMutation()

    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
    } = useFormik({
        initialValues: { email: '' },
        validationSchema: newsletterValidationSchema,
        onSubmit: async (formValues) => {
            try {
                const response = await subscribeToNewsletter({ email: formValues.email.trim() }).unwrap()
                toast.success(response.message || 'Subscribed successfully')
                resetForm()
            } catch (error: unknown) {
                console.error('Error subscribing to newsletter', error)
                toast.danger('Failed to subscribe. Please try again.')
            }
        },
    })

    const openSignInModal = () => {
        dispatch(openModal({
            componentName: 'LoginSignupIndex',
            data: {
                componentName: 'SignIn',
            },
            modalSize: 'full',
            modalPadding: 'p-0',
        }))
    }

    const handleAccountLink = (href: string) => {
        if (isAuthenticated) {
            router.push(href)
            return
        }
        openSignInModal()
    }

    const accountLinkClassName = 'text-sm text-white/60 hover:text-white transition-colors'

    const socialLinks = [
        { icon: <TwitterXSVG />, href: xLink, label: 'X (Twitter)' },
        { icon: <FooterInstagramSVG />, href: instagramLink, label: 'Instagram' },
        { icon: <LinkedInSVG />, href: linkedInLink, label: 'LinkedIn' },
    ]
    return (
        <>
            <footer className={`bg-black text-white ${showMobileBottomNav ? 'mb-[calc(3.5rem+env(safe-area-inset-bottom))] lg:mb-0' : ''}`}>
                <div className='custom_container'>
                    {/* Main footer content */}
                    <div className="py-10 md:py-16">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
                            {/* Brand column */}
                            <div className='md:col-span-6'>
                                <div className="space-y-6 md:w-2/3">
                                    <Link href="/" className="block">
                                        <div className="w-32 h-10">
                                            <ImageComponent src="/images/main/logo.svg" alt="VALLOTÉ" object_cover={false} priority={false} />
                                        </div>
                                    </Link>
                                    <p className="text-sm text-white/50 max-w-xs">{platformDescription || ''}</p>
                                    <form onSubmit={handleSubmit} className="mt-2 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email address"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                aria-invalid={Boolean(errors.email && touched.email)}
                                                className={`flex-1 min-w-0 bg-transparent border rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none transition-colors ${errors.email && touched.email
                                                        ? 'border-red-400/80 focus:border-red-400'
                                                        : 'border-white/30 focus:border-white/60'
                                                    }`}
                                            />
                                            <Button type="submit" isDisabled={isSubscribing} className={`global_btn rounded_full bg_white shrink-0`}>
                                                {isSubscribing ? 'Connecting…' : 'Connect'}
                                            </Button>
                                        </div>
                                        {errors.email && touched.email && (
                                            <p className="text-xs text-red-400">{errors.email}</p>
                                        )}
                                    </form>
                                </div>
                            </div>

                            <div className='md:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-8 md:gap-0'>
                                {/* Shop column */}
                                <div className="space-y-5">
                                    <h4 className="font-medium tracking-widest uppercase">
                                        Shop
                                    </h4>
                                    <ul className="space-y-2">
                                        {shopLinks.map((link) => (
                                            <li key={link.href}>
                                                <Link
                                                    href={link.href}
                                                    className="text-sm text-white/60 hover:text-white transition-colors"
                                                >
                                                    {link.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Account column */}
                                <div className="space-y-5">
                                    <h4 className="font-medium tracking-widest uppercase">
                                        Account
                                    </h4>
                                    <ul className="space-y-2">
                                        {accountLinks.map((link) => (
                                            <li key={link.href}>
                                                {isAuthenticated ? (
                                                    <Link
                                                        href={link.href}
                                                        className={accountLinkClassName}
                                                    >
                                                        {link.label}
                                                    </Link>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAccountLink(link.href)}
                                                        className={`${accountLinkClassName} cursor-pointer`}
                                                    >
                                                        {link.label}
                                                    </button>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* About column */}
                                <div className="space-y-5">
                                    <h4 className="font-medium tracking-widest uppercase">
                                        About
                                    </h4>
                                    <ul className="space-y-2">
                                        {aboutLinks.map((link) => (
                                            <li key={link.href}>
                                                <Link
                                                    href={link.href}
                                                    className="text-sm text-white/60 hover:text-white transition-colors"
                                                >
                                                    {link.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom bar */}
                    <div className="border-t border-white/10">
                        <div className="py-8 md:py-16 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <p className="text-sm text-white/40">
                                © 2026 VALLOTÉ. All rights reserved.
                            </p>
                            <div className="flex items-center gap-4">
                                {socialLinks.map((social, index) => (
                                    <Link
                                        key={index}
                                        href={social?.href ?? ''}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={social?.label ?? ''}
                                        className="text-white/50 hover:text-white transition-colors"
                                    >
                                        {social?.icon}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Half title */}
                    <div className="overflow-hidden font-secondary">
                        <ValloteLogoSVG />
                    </div>
                </div>
            </footer>
        </>
    )
}
